import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useHasPermission } from "./session-context"
import { SearchIcon } from "./components/icons"
import { NominationsPageSkeleton } from "./components/skeleton"
import { StatusTabs } from "./components/status-tabs"
import { ContentPagination } from "./content-components"

type NominationStatus = "new" | "under_review" | "verified" | "rejected"
const PAGE_SIZE = 10

interface NominationListItem {
  id: string
  code: string
  childName: string
  age: number
  currentClass: string | null
  location: string
  program: "scholarship" | "ability"
  nominatorName: string
  nominatorTitle: string | null
  submittedAt: string
  status: NominationStatus
}

interface StatusCounts {
  all: number
  new: number
  under_review: number
  verified: number
  rejected: number
}

const STATUS_COLORS: Record<NominationStatus, { bg: string; text: string }> = {
  new: { bg: "bg-gold-100", text: "text-gold-700" },
  under_review: { bg: "bg-blue-100", text: "text-blue-700" },
  verified: { bg: "bg-green-100", text: "text-green-700" },
  rejected: { bg: "bg-red-100", text: "text-red-700" },
}

const STATUS_LABELS: Record<NominationStatus, string> = {
  new: "New",
  under_review: "Under review",
  verified: "Verified",
  rejected: "Rejected",
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toISOString().split("T")[0] ?? dateString
}

export function NominationsPage() {
  const canView = useHasPermission("nominations")
  const navigate = useNavigate()
  const [nominations, setNominations] = useState<NominationListItem[]>([])
  const [counts, setCounts] = useState<StatusCounts>({ all: 0, new: 0, under_review: 0, verified: 0, rejected: 0 })
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<NominationStatus | "all">("all")
  const [search, setSearch] = useState("")

  const fetchNominations = async () => {
    try {
      const query: { status?: NominationStatus; search?: string; limit: string; offset: string } = {
        limit: String(PAGE_SIZE),
        offset: String((page - 1) * PAGE_SIZE),
      }
      if (activeTab !== "all") query.status = activeTab
      if (search.trim()) query.search = search.trim()

      const res = await client.api.admin.nominations.$get({ query })
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        setNominations(body.data.items)
        setCounts(body.data.counts)
        setTotal(body.data.total)
      }
    } catch {
      setError("Failed to load nominations")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (canView) {
      void fetchNominations()
    } else {
      setLoading(false)
    }
  }, [activeTab, canView, page])

  useEffect(() => {
    setPage(1)
  }, [activeTab, search])

  // Debounced search
  useEffect(() => {
    if (!canView) return
    const timer = setTimeout(() => {
      void fetchNominations()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const handleRowClick = (id: string) => {
    navigate(`/admin/nominations/${id}`)
  }

  if (!canView) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Nominations</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to view nominations.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <NominationsPageSkeleton />
  }

  const tabs: { key: NominationStatus | "all"; label: string; count: number }[] = [
    { key: "all", label: "All", count: counts.all },
    { key: "under_review", label: "Under review", count: counts.under_review },
    { key: "verified", label: "Verified", count: counts.verified },
    { key: "rejected", label: "Rejected", count: counts.rejected },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-forest-950 sm:text-4xl">Nominations</h1>
        <p className="mt-1 text-sm text-ink/60">Referrals awaiting verification for the program.</p>
      </div>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <StatusTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel="Nomination status"
        />

        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-10 w-full rounded-full border border-gold-500 bg-cream-50 pl-10 pr-4 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none sm:w-64"
          />
        </div>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : nominations.length === 0 ? (
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">No nominations found</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-gold-500 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gold-500/50">
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    ID
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Child
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Location
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Nominator
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Submitted
                  </th>
                  <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-ink/60">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gold-500/30">
                {nominations.map((nomination) => (
                  <tr
                    key={nomination.id}
                    onClick={() => handleRowClick(nomination.id)}
                    className="cursor-pointer hover:bg-cream-50"
                  >
                    <td className="px-5 py-4 text-sm text-ink/60">{nomination.code}</td>
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-forest-700">{nomination.childName}</p>
                        <p className="text-xs text-ink/50">Age {nomination.age}</p>
                        {nomination.currentClass && <p className="text-xs text-ink/50">Class {nomination.currentClass}</p>}
                        <p className="text-xs text-ink/50">{nomination.program === "ability" ? "Ability Program" : "Scholarship"}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{nomination.location}</td>
                    <td className="px-5 py-4">
                      <div>
                        <p className="text-sm font-medium text-forest-700">{nomination.nominatorName}</p>
                        <p className="text-xs text-ink/50">{nomination.nominatorTitle || "—"}</p>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-sm text-ink">{formatDate(nomination.submittedAt)}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          STATUS_COLORS[nomination.status].bg
                        } ${STATUS_COLORS[nomination.status].text}`}
                      >
                        {STATUS_LABELS[nomination.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ContentPagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} disabled={loading} />
    </div>
  )
}
